// src/pages/facilities/BarLoungeMenuPage.js
import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { menuData, ingredientIcons } from '../../data/barMenuData';
import MenuItem from '../../components/barLounge/MenuItem';
import CocktailItem from '../../components/barLounge/CocktailItem';
import BarAvailabilityStatus from '../../components/barLounge/BarAvailabilityStatus';
import squirrelImage from '../../assets/images/squirrel_placeholder.png'; // 画像をインポート

const BarLoungeMenuPage = () => {
  const { fingerFood, todaysSpecials, drinks } = menuData;

  // Tab navigation state
  const [activeTab, setActiveTab] = useState('all');
  const [skipFilters, setSkipFilters] = useState(false);
  
  // 通常のフィルター関連のstate
  const [selectedIngredients, setSelectedIngredients] = useState(new Set());

  // Tab click handler that skips filters
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setSkipFilters(true);
    setSelectedIngredients(new Set()); // Clear any existing filters
    
    // Scroll to the relevant section after a brief delay to allow for state updates
    setTimeout(() => {
      let targetSection = null;
      
      switch (tabName) {
        case 'food':
          targetSection = document.querySelector('.finger-food-title');
          break;
        case 'cocktails':
          // Look for cocktails section
          targetSection = document.querySelector('h3[style*="color: rgb(40, 167, 69)"]') || 
                         document.querySelector('.menu-section h3');
          break;
        case 'non-alcoholic':
        case 'spirits':
        case 'all':
        default:
          // Scroll to the first menu section
          targetSection = document.querySelector('.menu-section');
          break;
      }
      
      if (targetSection) {
        targetSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // カクテル提案機能関連のstate
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [suggestedCocktails, setSuggestedCocktails] = useState([]);

  // フィルターとして利用可能な材料をカテゴリー分け
  const categorizedIngredients = useMemo(() => {
    const categories = {
      "Spirits": [
        "ジン", "ウォッカ", "ラム", "テキーラ", "ウイスキー", "バーボン",
        "スコッチウイスキー", "ライウイスキー", "ブランデー", "アップルブランデー", "コニャック"
      ],
      "Liqueurs": [
        "カシスリキュール", "ピーチリキュール", "カンパリ", "アマレット", "コアントロー",
        "ホワイトキュラソー", "ブルーキュラソー", "ライチリキュール", "マリブリキュール",
        "チェリーブランデー", "ドライベルモット", "スイートベルモット", "クレームドカシス",
        "オレンジキュラソー"
      ],
      "Mixers & Juices": [
        "トニックウォーター", "ソーダ", "コーラ", "ジンジャーエール", "オレンジジュース",
        "レモンジュース", "ライムジュース", "グレープフルーツジュース", "パイナップルジュース",
        "トマトジュース", "クランベリージュース", "レモネード"
      ],
      "Wine & Beer": [
        "ビール", "赤ワイン", "白ワイン"
      ],
      "Others": [ // Finger Food関連やその他の材料
        "砂糖", "塩", "グレナデンシロップ", "アンゴスチュラビターズ", "ライム", "レモン",
        "マカダミアナッツ", "チーズ", "ベーコン", "ハチミツ", "ローズマリー", "ガーリック", "バジル",
        "ココナッツ", "ローストバナナ", "野菜", "ドライフルーツ", "チョコレート"
      ]
    };
    return categories;
  }, []);

  // フィルター選択ハンドラ
  const handleFilterChange = (ingredient) => {
    setSelectedIngredients(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(ingredient)) {
        newSelected.delete(ingredient);
      } else {
        newSelected.add(ingredient);
      }
      return newSelected;
    });
  };

  // メニュー項目をフィルターする関数
  const filterMenuItem = useCallback((item) => {
    // フィルターが何も選択されていなければ全て表示
    if (selectedIngredients.size === 0) {
      return true;
    }

    // 焼酎はフィルターが選択されている場合は表示しない
    if (item.type === "shochu") {
      return false;
    }

    const itemIngredients = item.ingredients || [];

    // 選択されたフィルターのいずれか一つでもアイテムの材料に含まれていれば表示 (OR条件)
    return Array.from(selectedIngredients).some(selectedIng =>
      itemIngredients.some(itemIng => itemIng.toLowerCase().includes(selectedIng.toLowerCase()))
    );
  },  [selectedIngredients]);

  // Finger Foodのタイトルにリスの画像を表示するためのスタイル
  const fingerFoodTitleStyle = {
    backgroundImage: `url(${squirrelImage})`,
  };

  // 全メニューをフラット化してフィルター処理しやすい形にする
  const flattenedMenu = useMemo(() => {
    const list = [];

    // Finger Foodセクションを処理
    fingerFood.forEach(category => {
      category.items.forEach(item => list.push({ ...item, type: "fingerFood", category: category.name }));
    });

    // Todays Specialsセクションを処理
    todaysSpecials.forEach(item => list.push({ ...item, type: "todaysSpecial", category: "todaysSpecials" }));

    // Drinksセクションを処理
    for (const categoryKey in drinks) {
      const category = drinks[categoryKey];

      if (categoryKey === "shochu") {
        category.types.forEach(type => {
          type.items.forEach(item => list.push({ ...item, type: "shochu", category: category.title, subCategory: type.type }));
        });
      } else if (categoryKey === "spirits" || categoryKey === "whisky") {
        for (const subCategoryKey in category) {
          if (subCategoryKey === "options") continue;
          category[subCategoryKey].forEach(item => list.push({ ...item, type: "drink", category: categoryKey, subCategory: subCategoryKey }));
        }
      } else if (categoryKey === "cocktails") {
        for (const baseKey in category) {
          category[baseKey].forEach(item => list.push({ ...item, type: "cocktail", category: categoryKey, subCategory: baseKey }));
        }
      } else if (categoryKey === "nonAlcoholic") {
        category.forEach(item => list.push({ ...item, type: "nonAlcoholic", category: categoryKey }));
      } else { // beer, wine, brandy, rum, tequila, softDrink
        category.forEach(item => list.push({ ...item, type: "drink", category: categoryKey }));
      }
    }
    return list;
  }, [fingerFood, todaysSpecials, drinks]);

  // フィルターされたメニュー項目
  const filteredItems = useMemo(() => {
    return flattenedMenu.filter(filterMenuItem);
  }, [flattenedMenu, filterMenuItem]); // MODIFIED: Added filterMenuItem to dependencies, removed selectedIngredients as filterMenuItem already depends on it.

  // Tab filtering logic
  const getTabFilteredItems = useMemo(() => {
    if (activeTab === 'all') return filteredItems;
    
    return filteredItems.filter(item => {
      switch (activeTab) {
        case 'non-alcoholic':
          return item.type === 'nonAlcoholic' || 
                 (item.category === 'softDrink' && item.type === 'drink');
        case 'cocktails':
          return item.type === 'cocktail';
        case 'spirits':
          return item.type === 'drink' && 
                 !['softDrink'].includes(item.category);
        case 'food':
          return item.type === 'fingerFood' || item.type === 'todaysSpecial';
        default:
          return true;
      }
    });
  }, [filteredItems, activeTab]);

  // フィルターされたアイテムを元のカテゴリ構造に戻す（表示のため）
  const groupedFilteredItems = useMemo(() => {
    const grouped = {
      fingerFood: [],
      todaysSpecials: [], // Add todaysSpecials here
      beer: [], wine: [], brandy: [], rum: [], tequila: [],
      shochu: { types: [], options: drinks.shochu.options },
      spirits: { gin: [], vodka: [] },
      whisky: { bourbon: [], scotch: [], canadian: [], irish: [], japanese: [], options: drinks.whisky.options },
      softDrink: [],
      cocktails: {},
      nonAlcoholic: []
    };

    getTabFilteredItems.forEach(item => {
      if (item.type === "fingerFood") {
        let categoryGroup = grouped.fingerFood.find(cat => cat.name === item.category);
        if (!categoryGroup) {
          categoryGroup = { name: item.category, items: [] };
          grouped.fingerFood.push(categoryGroup);
        }
        categoryGroup.items.push(item);
      } else if (item.type === "todaysSpecial") {
        grouped.todaysSpecials.push(item);
      } else if (item.type === "shochu") {
        let typeGroup = grouped.shochu.types.find(t => t.type === item.subCategory);
        if (!typeGroup) {
          typeGroup = { type: item.subCategory, items: [] };
          grouped.shochu.types.push(typeGroup);
        }
        typeGroup.items.push(item);
      } else if (item.category === "spirits" || item.category === "whisky") {
        if (grouped[item.category] && grouped[item.category][item.subCategory]) {
          grouped[item.category][item.subCategory].push(item);
        }
      } else if (item.category === "cocktails") {
        if (!grouped.cocktails[item.subCategory]) {
          grouped.cocktails[item.subCategory] = [];
        }
        grouped.cocktails[item.subCategory].push(item);
      } else if (item.category === "nonAlcoholic") {
        grouped.nonAlcoholic.push(item);
      } else {
        if (grouped[item.category]) {
          grouped[item.category].push(item);
        }
      }
    });

    // 空のカテゴリを削除（表示しない）
    for (const key in grouped) {
      if (Array.isArray(grouped[key])) {
        if (key === "fingerFood") {
          const hasVisibleItems = grouped[key].some(cat => cat.items.length > 0);
          if (!hasVisibleItems) {
            delete grouped[key];
          }
        } else if (key === "fingerFood") {
          const hasVisibleItems = grouped[key].some(cat => cat.items.length > 0);
          if (!hasVisibleItems) {
            delete grouped[key];
          }
        } else if (key === "todaysSpecials") {
          if (grouped[key].length === 0) {
            delete grouped[key];
          }
        } else if (grouped[key].length === 0) {
          delete grouped[key];
        }
      } else if (typeof grouped[key] === 'object' && !Array.isArray(grouped[key])) {
        // shochuの場合、selectedIngredients.size > 0 の場合 typesが空になるため、shochuカテゴリ全体を削除
        if (key === "shochu" && selectedIngredients.size > 0 && grouped[key].types.length === 0) {
            delete grouped[key];
            continue;
        }

        let allSubCategoriesEmpty = true;
        for (const subKey in grouped[key]) {
          if (subKey === 'options') continue;
          if (Array.isArray(grouped[key][subKey]) && grouped[key][subKey].length > 0) {
            allSubCategoriesEmpty = false;
            break;
          }
        }
        if (allSubCategoriesEmpty) {
          delete grouped[key];
        }
      }
    }

    return grouped;
  }, [getTabFilteredItems, selectedIngredients, drinks.shochu.options, drinks.whisky.options]);


  // 各ドリンクカテゴリをレンダリングするヘルパー関数
  const renderDrinksCategory = (items, type) => {
    if (!items || (Array.isArray(items) && items.length === 0) ||
        (typeof items === 'object' && !Array.isArray(items) &&
         Object.keys(items).every(k => (Array.isArray(items[k]) && items[k].length === 0) || k === 'options' || (k === 'types' && items[k].length === 0) ) )) {
      return null;
    }

    if (type === "shochu") {
      const shochuOptions = drinks.shochu.options;
      return (
        <div key={type}>
          <h4 className="menu-category-title">{drinks.shochu.title}</h4>
          {items.types.map((typeGroup, index) => (
            <div key={index}>
              <p className="shochu-type-title">{typeGroup.type}</p>
              {typeGroup.items.map((item, idx) => (
                <MenuItem key={idx} name={item.name} price={item.price} glass={item.glass} />
              ))}
            </div>
          ))}
          {shochuOptions && shochuOptions.length > 0 && (
            <>
              <p className="shochu-type-title">Options</p>
              <ul className="options-list">
                {shochuOptions.map((opt, idx) => (
                  <li key={idx}><span>{opt.name}</span><span>{opt.price}</span></li>
                ))}
              </ul>
            </>
          )}
        </div>
      );
    } else if (type === "spirits" || type === "whisky") {
      const options = type === "whisky" ? drinks.whisky.options : null;
      return (
        <div key={type}>
          <h4 className="menu-category-title">{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
          {Object.entries(items).map(([subType, subItems]) => {
            if (subType === "options") return null;
            if (subItems.length === 0) return null;
            const formattedSubType = subType.charAt(0).toUpperCase() + subType.slice(1);
            return (
              <div key={subType}>
                <h5 className="menu-subcategory-title">{formattedSubType}</h5>
                {subItems.map((item, index) => (
                  <CocktailItem key={index} name={item.name} price={item.price} glass={item.glass} ingredients={item.ingredients} />
                ))}
              </div>
            );
          })}
          {options && options.length > 0 && (
            <>
              <h5 className="menu-subcategory-title">Options</h5>
              <ul className="options-list">
                {options.map((opt, idx) => (
                  <li key={idx}><span>{opt.name}</span><span>{opt.price}</span></li>
                ))}
              </ul>
            </>
          )}
        </div>
      );
    } else if (type === "cocktails") {
      return (
        <section className="menu-section">
          <h3 className="section-title">Cocktails</h3>
          {Object.entries(items).map(([base, cocktails]) => {
            if (cocktails.length === 0) return null;
            const formattedBase = base
              .replace(/([A-Z])/g, ' $1')
              .replace('Base', ' Base')
              .trim()
              .replace(/\b\w/g, l => l.toUpperCase());
            return (
              <div key={base}>
                <h4 className="menu-category-title">{formattedBase}</h4>
                {cocktails.map((cocktail, index) => (
                  <CocktailItem
                    key={index}
                    name={cocktail.name}
                    price={cocktail.price}
                    ingredients={cocktail.ingredients}
                    glass={cocktail.glass}
                  />
                ))}
              </div>
            );
          })}
        </section>
      );
    } else if (type === "nonAlcoholic") {
      return (
        <section className="menu-section">
          <h3 className="section-title">ノンアルコールカクテル</h3>
          {items.map((item, index) => (
            <CocktailItem
              key={index}
              name={item.name}
              price={item.price}
              ingredients={item.ingredients}
              glass={item.glass}
            />
          ))}
        </section>
      );
    } else {
      const formattedTitle = type.charAt(0).toUpperCase() + type.slice(1);
      return (
        <div key={type}>
          <h4 className="menu-category-title">{formattedTitle}</h4>
          {items.map((item, index) => (
            <CocktailItem key={index} name={item.name} price={item.price} glass={item.glass} ingredients={item.ingredients} />
          ))}
        </div>
      );
    }
  };

  // フィルター結果がない場合のメッセージ
  const noResults = selectedIngredients.size > 0 && getTabFilteredItems.length === 0;
  const isTodaysSpecialsVisible = Object.prototype.hasOwnProperty.call(groupedFilteredItems, 'todaysSpecials');
  const isFingerFoodVisible = Object.prototype.hasOwnProperty.call(groupedFilteredItems, 'fingerFood');

  // I'm feeling lucky 機能
  const questions = useMemo(() => [
    {
      id: 'flavor',
      text: 'どんな風味がお好みですか？',
      neutralOption: { value: 'any_flavor', label: '特にこだわりはない', icon: '❓' },
      options: [
        { value: 'fruity', label: 'フルーティーな甘さ', icon: '🍓' },
        { value: 'refreshing', label: 'さっぱり爽やか', icon: '🧊' },
        { value: 'sweet', label: '甘口（デザート感覚）', icon: '🍯' },
        { value: 'strong_alcohol', label: 'アルコール強め', icon: '💪' },
        { value: 'spicy', label: 'スパイシーな風味', icon: '🔶' },
      ]
    },
    {
      id: 'base',
      text: 'ベースとなるお酒は？',
      neutralOption: { value: 'any_base', label: '特にこだわりはない', icon: '❓' },
      options: [
        { value: 'ジン', label: 'ジン', icon: ingredientIcons["ジン"] },
        { value: 'ウォッカ', label: 'ウォッカ', icon: ingredientIcons["ウォッカ"] },
        { value: 'ラム', label: 'ラム', icon: ingredientIcons["ラム"] },
        { value: 'テキーラ', label: 'テキーラ', icon: ingredientIcons["テキーラ"] },
        { value: 'ウイスキー', label: 'ウイスキー', icon: ingredientIcons["ウイスキー"] },
        { value: 'ブランデー', label: 'ブランデー', icon: ingredientIcons["ブランデー"] },
        { value: 'ワイン', label: 'ワイン', icon: ingredientIcons["赤ワイン"] },
        { value: 'リキュール', label: 'リキュール', icon: ingredientIcons["オレンジキュラソー"] },
      ]
    },
    {
      id: 'glass',
      text: 'どんなスタイルのカクテルがお好みですか？',
      neutralOption: { value: 'any_glass', label: 'どちらでもよい', icon: '↔️' },
      options: [
        { value: 'short', label: 'きりっとショートカクテル（小さめ）', icon: '🍸' },
        { value: 'long', label: 'ごくごくロングカクテル（大きめ）', icon: '🍹' },
      ]
    }
  ], []);

  // 全カクテルをフラット化 (提案ロジック用)
  const allCocktails = useMemo(() => {
    const list = [];
    for (const baseKey in drinks.cocktails) {
      drinks.cocktails[baseKey].forEach(item => list.push({ ...item, type: "cocktail" }));
    }
    drinks.nonAlcoholic.forEach(item => list.push({ ...item, type: "nonAlcoholic" }));
    return list;
  }, [drinks]);

  // カクテルの特徴を判定するヘルパー関数
  const getCocktailCharacteristics = useCallback((cocktail) => {
    const ingredients = cocktail.ingredients || [];
    const glass = cocktail.glass;
    const characteristics = {
      fruity: false,
      refreshing: false,
      sweet: false,
      strong_alcohol: false,
      spicy: false,
      base: 'none', // 'none'はデフォルト値。'any_base'とは別。
      glass_type: 'any',
    };

    const fruityIngredients = ["オレンジジュース", "パイナップルジュース", "クランベリージュース", "ピーチリキュール", "ライチリキュール", "チェリーブランデー", "グレナデンシロップ"];
    const refreshingIngredients = ["ソーダ", "トニックウォーター", "レモンジュース", "ライムジュース", "ライム", "レモン"];
    const sweetIngredients = ["砂糖", "グレナデンシロップ", "カシスリキュール", "ピーチリキュール", "マリブリキュール", "アマレット", "レモネード", "コアントロー", "オレンジキュラソー", "ホワイトキュラソー", "ブルーキュラソー", "ライチリキュール", "クレームドカシス", "チェリーブランデー", "スイートベルモット"];
    const spicyIngredients = ["ジンジャーエール", "トマトジュース", "塩", "アンゴスチュラビターズ"];

    if (ingredients.some(ing => fruityIngredients.some(fIng => ing.toLowerCase().includes(fIng.toLowerCase())))) {
      characteristics.fruity = true;
    }
    if (ingredients.some(ing => refreshingIngredients.some(rIng => ing.toLowerCase().includes(rIng.toLowerCase())))) {
      characteristics.refreshing = true;
    }
    if (ingredients.some(ing => sweetIngredients.some(sIng => ing.toLowerCase().includes(sIng.toLowerCase())))) {
      characteristics.sweet = true;
    }
    if (ingredients.some(ing => spicyIngredients.some(sIng => ing.toLowerCase().includes(sIng.toLowerCase())))) {
      characteristics.spicy = true;
    }

    const spirits = ["ジン", "ウォッカ", "ラム", "テキーラ", "ウイスキー", "バーボン", "スコッチウイスキー", "ライウイスキー", "ブランデー", "コニャック", "アップルブランデー"];
    const strongAlcoholAdditives = ["ドライベルモット", "スイートベルモット", "アンゴスチュラビターズ"];
    const mixerIngredients = ["トニックウォーター", "ソーダ", "コーラ", "ジンジャーエール", "オレンジジュース", "レモンジュース", "ライムジュース", "グレープフルーツジュース", "パイナップルジュース", "トマトジュース", "クランベリージュース", "レモネード"];

    const baseSpiritsCount = ingredients.filter(ing => spirits.some(s => ing.toLowerCase().includes(s.toLowerCase()))).length;
    const hasMixers = ingredients.some(ing => mixerIngredients.some(m => ing.toLowerCase().includes(m.toLowerCase())));
    const hasStrongAdditive = ingredients.some(ing => strongAlcoholAdditives.some(s => ing.toLowerCase().includes(s.toLowerCase())));

    // 強アルコール判定のロジックを調整: ノンアルコールカクテルはstrong_alcoholにはならない
    // スピリッツが使われていて、ミキサーが少ないか、強い副材料が入っている場合
    if (cocktail.type !== "nonAlcoholic" && baseSpiritsCount >= 1 && (!hasMixers || hasStrongAdditive)) {
      characteristics.strong_alcohol = true;
    }

    if (ingredients.some(ing => ing.toLowerCase().includes("ジン"))) characteristics.base = 'ジン';
    else if (ingredients.some(ing => ing.toLowerCase().includes("ウォッカ"))) characteristics.base = 'ウォッカ';
    else if (ingredients.some(ing => ing.toLowerCase().includes("ラム"))) characteristics.base = 'ラム';
    else if (ingredients.some(ing => ing.toLowerCase().includes("テキーラ"))) characteristics.base = 'テキーラ';
    else if (ingredients.some(ing => ["ウイスキー", "バーボン", "スコッチウイスキー", "ライウイスキー"].some(w => ing.toLowerCase().includes(w.toLowerCase())))) characteristics.base = 'ウイスキー';
    else if (ingredients.some(ing => ["ブランデー", "アップルブランデー", "コニャック"].some(b => ing.toLowerCase().includes(b.toLowerCase())))) characteristics.base = 'ブランデー';
    else if (ingredients.some(ing => ["赤ワイン", "白ワイン"].some(w => ing.toLowerCase().includes(w.toLowerCase())))) characteristics.base = 'ワイン';
    else if (ingredients.some(ing => ["カシスリキュール", "ピーチリキュール", "カンパリ", "アマレット", "コアントロー", "ホワイトキュラソー", "ブルーキュラソー", "ライチリキュール", "マリブリキュール", "チェリーブランデー", "ドライベルモット", "スイートベルモット", "クレームドカシス", "オレンジキュラソー"].some(l => ing.toLowerCase().includes(l.toLowerCase())))) characteristics.base = 'リキュール';
    else if (cocktail.type === "nonAlcoholic") characteristics.base = 'none'; // ノンアルコールはベースなし。`any_base`とは別。

    if (glass === "🍸") {
      characteristics.glass_type = 'short';
    } else if (glass === "🍹" || glass === "🍺" || glass === "🍷" || glass === "🥤") {
      characteristics.glass_type = 'long';
    }

    return characteristics;
  }, []);

  // 提案を生成するロジック
  const generateSuggestions = useCallback((answers) => {
    // 全ての質問で「どっちつかず」が選ばれたかチェック
    const allNeutral = Object.values(answers).every(answer =>
      answer === 'any_flavor' || answer === 'any_base' || answer === 'any_glass'
    );

    if (allNeutral) {
      // 全てどっちつかずの場合は完全にランダムに3件表示
      const shuffled = [...allCocktails].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, 3);
    }

    let matchedCocktails = [];

    allCocktails.forEach(cocktail => {
      const chars = getCocktailCharacteristics(cocktail);
      let matchScore = 0;

      // 風味の判定 (中立オプションでない場合のみスコア加算)
      if (answers.flavor && answers.flavor !== 'any_flavor') {
        if (answers.flavor === 'fruity' && chars.fruity) matchScore++;
        else if (answers.flavor === 'refreshing' && chars.refreshing) matchScore++;
        else if (answers.flavor === 'sweet' && chars.sweet) matchScore++;
        else if (answers.flavor === 'strong_alcohol' && chars.strong_alcohol) matchScore++;
        else if (answers.flavor === 'spicy' && chars.spicy) matchScore++;
      }

      // ベースのお酒の判定 (中立オプションでない場合のみスコア加算)
      if (answers.base && answers.base !== 'any_base') {
        if (chars.base.toLowerCase() === answers.base.toLowerCase()) {
          matchScore++;
        }
      }

      // グラススタイルの判定 (中立オプションでない場合のみスコア加算)
      if (answers.glass && answers.glass !== 'any_glass') {
        if (chars.glass_type === answers.glass) {
          matchScore++;
        }
      }

      // ノンアルコールカクテルはアルコール強めと選択された場合は除外
      if (cocktail.type === "nonAlcoholic" && answers.flavor === 'strong_alcohol') {
        matchScore = 0; // スコアを0にして強制的に除外
      }

      if (matchScore > 0) {
        matchedCocktails.push({ cocktail, matchScore });
      }
    });

    matchedCocktails.sort((a, b) => b.matchScore - a.matchScore);

    const topSuggestions = [];
    if (matchedCocktails.length > 0) {
        const highestScore = matchedCocktails[0].matchScore;
        matchedCocktails.forEach(item => {
            if (item.matchScore === highestScore) {
                topSuggestions.push(item.cocktail);
            }
        });
    }

    // 最高スコアのカクテルが少ない場合は、他の高スコアのカクテルも追加する
    // ただし、トップ5件まで
    if (topSuggestions.length < 5 && matchedCocktails.length > topSuggestions.length) {
        let currentSuggestionCount = topSuggestions.length;
        for(let i = topSuggestions.length; i < matchedCocktails.length && currentSuggestionCount < 5; i++) {
            topSuggestions.push(matchedCocktails[i].cocktail);
            currentSuggestionCount++;
        }
    }

    // 重複を排除し、最終的に最大5件に制限
    const uniqueSuggestions = Array.from(new Set(topSuggestions.map(JSON.stringify))).map(JSON.parse);
    return uniqueSuggestions.slice(0, 5);
  }, [allCocktails, getCocktailCharacteristics]);


  // モーダル関連ハンドラ
  const handleFeelingLuckyClick = () => {
    setShowSuggestionModal(true);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setSuggestedCocktails([]); // 提案結果をリセット
    setSelectedIngredients(new Set()); // 通常フィルターをリセット
  };

  const handleModalClose = () => {
    setShowSuggestionModal(false);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setSuggestedCocktails([]); // モーダルを閉じたら提案もリセット
  };

  const handleAnswer = (questionId, value) => {
    // 回答を即座に更新
    const nextUserAnswers = { ...userAnswers, [questionId]: value };
    setUserAnswers(nextUserAnswers);

    // 回答したら自動的に次の質問へ遷移
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // 最後の質問に答えたら提案を生成
      const suggestions = generateSuggestions(nextUserAnswers); // 更新された回答で提案を生成
      setSuggestedCocktails(suggestions);
      setShowSuggestionModal(false); // モーダルを閉じる
    }
  };

  const handlePrevQuestion = () => {
    setCurrentQuestionIndex(prev => prev - 1);
  };

  const handleResetSuggestion = () => {
    setSuggestedCocktails([]); // 提案結果をクリアして通常メニューに戻す
  };

  const currentQuestion = questions[currentQuestionIndex];


  return (
    <>
      <header className="page-header">
        <h1>施設案内</h1>
        <h2>バーラウンジ - Drink & Food Menu</h2>
      </header>

      {/* BAR Availability Status */}
      <BarAvailabilityStatus />

      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <div className="tab-container">
          <button 
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabClick('all')}
          >
            <span className="tab-icon">🍽️</span>
            <span className="tab-label">すべて</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'non-alcoholic' ? 'active' : ''}`}
            onClick={() => handleTabClick('non-alcoholic')}
          >
            <span className="tab-icon">🥤</span>
            <span className="tab-label">ノンアルコール</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'cocktails' ? 'active' : ''}`}
            onClick={() => handleTabClick('cocktails')}
          >
            <span className="tab-icon">🍸</span>
            <span className="tab-label">カクテル</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'spirits' ? 'active' : ''}`}
            onClick={() => handleTabClick('spirits')}
          >
            <span className="tab-icon">🥃</span>
            <span className="tab-label">スピリッツ & ワイン</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'food' ? 'active' : ''}`}
            onClick={() => handleTabClick('food')}
          >
            <span className="tab-icon">🍴</span>
            <span className="tab-label">フード</span>
          </button>
          <div className="tab-indicator"></div>
        </div>
      </nav>

      {/* I'm feeling lucky ボタン */}
      <section className="feeling-lucky-section">
        <button className="feeling-lucky-button" onClick={handleFeelingLuckyClick}>
          <span role="img" aria-label="dice">🎲</span> 今日の気分でおすすめカクテルを探す
        </button>
        <Link
          to="/facilities/bar-lounge/custom?source=menu_cta"
          className="custom-builder-button"
        >
          <span role="img" aria-label="custom">🧪</span> カスタムカクテルページへ
        </Link>
      </section>

      {/* カクテル提案モーダル */}
      {showSuggestionModal && (
        <div className="suggestion-modal-overlay">
          <div className="suggestion-modal-content">
            <button className="modal-close-button" onClick={handleModalClose}>✖</button>
            <h3 className="modal-question-title">{currentQuestion.text}</h3>
            <div className="modal-options">
              {/* 中央配置の中立オプション */}
              {currentQuestion.neutralOption && (
                <div className="neutral-option-container">
                    <button
                      key={currentQuestion.neutralOption.value}
                      className={`modal-option-button ${userAnswers[currentQuestion.id] === currentQuestion.neutralOption.value ? 'selected' : ''}`}
                      onClick={() => handleAnswer(currentQuestion.id, currentQuestion.neutralOption.value)}
                    >
                      {currentQuestion.neutralOption.icon && <span className="option-icon">{currentQuestion.neutralOption.icon}</span>}
                      {currentQuestion.neutralOption.label}
                    </button>
                </div>
              )}
              <div className="other-options-container"> {/* その他のオプション */}
                {currentQuestion.options.map(option => (
                  <button
                    key={option.value}
                    className={`modal-option-button ${userAnswers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                    onClick={() => handleAnswer(currentQuestion.id, option.value)}
                  >
                    {option.icon && <span className="option-icon">{option.icon}</span>}
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-navigation-buttons">
              {currentQuestionIndex > 0 && (
                <button className="back-button" onClick={handlePrevQuestion}>戻る</button>
              )}
              {/* 自動遷移のため「次へ」「決定」ボタンは不要。
                  ただし、デバッグや将来的な機能拡張のために、
                  手動遷移が必要な場合はここにボタンを配置する。
              */}
            </div>
          </div>
        </div>
      )}

      {/* 提案されたカクテルセクション、または通常のメニュー表示 */}
      {suggestedCocktails.length > 0 ? (
        <section className="suggested-cocktails-section">
          <h3 className="section-title">あなたへのおすすめカクテル</h3>
          <div className="suggested-cocktails-list">
            {suggestedCocktails.map((cocktail, index) => (
              <CocktailItem key={index} {...cocktail} />
            ))}
          </div>
          <div className="filter-clear-button-container">
            <button className="reset-suggestion-button" onClick={handleResetSuggestion}>
              もう一度選び直す / 通常メニューに戻る
            </button>
          </div>
        </section>
      ) : (
        <>
          {/* 通常のフィルターとメニュー */}
          <div className="tab-content active">
          {!skipFilters && (
            <section className="filter-section">
            <h3 className="section-title">フィルター</h3>
            <div className="filter-categories-container">
              {Object.entries(categorizedIngredients).map(([categoryName, ingredientsList]) => (
                <div key={categoryName} className="filter-category-group">
                  <h4 className="filter-category-title">{categoryName}</h4>
                  <div className="filter-tags">
                    {ingredientsList.map((ingredient) => {
                      if (!ingredientIcons[ingredient]) return null;
                      return (
                        <button
                          key={ingredient}
                          className={`filter-tag ${selectedIngredients.has(ingredient) ? 'selected' : ''}`}
                          onClick={() => handleFilterChange(ingredient)}
                        >
                          {ingredientIcons[ingredient]} {ingredient}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {selectedIngredients.size > 0 && (
              <div className="filter-clear-button-container">
                <button
                  className="filter-clear-button"
                  onClick={() => setSelectedIngredients(new Set())}
                >
                  フィルターをクリア
                </button>
              </div>
            )}
          </section>
          )}

          {skipFilters && (
            <div className="show-filters-button-container">
              <button 
                className="show-filters-button"
                onClick={() => setSkipFilters(false)}
              >
                フィルターを表示
              </button>
            </div>
          )}

          {isTodaysSpecialsVisible && (
            <section className="menu-section">
              <h3 className="section-title">本日のスペシャル</h3>
              {groupedFilteredItems.todaysSpecials.map((item, index) => (
                <MenuItem key={index}               name={item.name} price={item.price} description={item.description} />
              ))}
            </section>
          )}

          {isFingerFoodVisible && (
            <section className="menu-section">
              <h3 className="finger-food-title section-title" style={fingerFoodTitleStyle}>Finger Food</h3>
              {groupedFilteredItems.fingerFood.map((category, index) => (
                <div key={index}>
                  {category.items.length > 0 && <h4 className="menu-category-title">{category.name}</h4>}
                  {category.items.map((item, idx) => (
                    <MenuItem key={idx} name={item.name} price={item.price} />
                  ))}
                </div>
              ))}
            </section>
          )}

          {activeTab !== 'food' && (
            <section className="menu-section">
              <h3 className="section-title">Drinks</h3>
              {Object.keys(groupedFilteredItems).filter(key => key !== 'cocktails' && key !== 'nonAlcoholic' && key !== 'fingerFood').map(categoryKey => (
                renderDrinksCategory(groupedFilteredItems[categoryKey], categoryKey)
              ))}
            </section>
          )}

          {renderDrinksCategory(groupedFilteredItems.cocktails, 'cocktails')}
          {renderDrinksCategory(groupedFilteredItems.nonAlcoholic, 'nonAlcoholic')}

          {noResults && (
            <p className="no-results-message">選択された材料を含むメニューはありません。</p>
          )}
          </div>
        </>
      )}
    </>
  );
};

export default BarLoungeMenuPage;